import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

export default function StickyMenu({
  stickToRef,
  search,
  tabsRef,
  isStuck,
  tabs,
  activeTabId,
  onSelectTab,
  onScrollTabsNext,
  onScrollTabsPrev,
}) {
  const tabRefs = useRef({});
  const [showNext, setShowNext] = useState(false);
  const [showPrev, setShowPrev] = useState(false);
  const menuRef = useRef(null);
  const [fixedRect, setFixedRect] = useState(null);

  const fixedStyle = useMemo(() => {
    if (!isStuck || !fixedRect) return undefined;
    return {
      position: 'fixed',
      top: `calc(var(--app-header-h) + env(safe-area-inset-top, 0px))`,
      left: `${fixedRect.left}px`,
      width: `${fixedRect.width}px`,
    };
  }, [fixedRect, isStuck]);

  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;

    const applyHeight = () => {
      const h = Math.ceil(el.getBoundingClientRect().height || 0);
      if (h > 0) document.documentElement.style.setProperty('--rdp-menu-h', `${h}px`);
    };

    applyHeight();
    const ro = new ResizeObserver(() => applyHeight());
    ro.observe(el);
    window.addEventListener('resize', applyHeight);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', applyHeight);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isStuck) {
      setFixedRect(null);
      return;
    }

    const measure = () => {
      const host = stickToRef?.current || menuRef.current?.parentElement;
      if (!host) return;
      const r = host.getBoundingClientRect();
      // Use host's inner box for stable alignment.
      setFixedRect({ left: Math.round(r.left), width: Math.round(r.width) });
    };

    measure();
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [isStuck, stickToRef]);

  // Only show the chevron when the tabs row actually overflows
  // (otherwise it's clutter on wide viewports / short menus).
  useEffect(() => {
    const el = tabsRef?.current;
    if (!el) return undefined;
    const recompute = () => {
      const overflow = el.scrollWidth - el.clientWidth > 4;
      const atStart = el.scrollLeft <= 4;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      setShowNext(overflow && !atEnd);
      // Show the back button once the user has scrolled past the
      // beginning (so they can return). On desktop CSS hides it.
      setShowPrev(overflow && !atStart);
    };
    recompute();
    el.addEventListener('scroll', recompute, { passive: true });
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    window.addEventListener('resize', recompute);
    return () => {
      el.removeEventListener('scroll', recompute);
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [tabsRef, tabs.length]);

  // Keep the active tab visible inside the horizontal scroller.
  useEffect(() => {
    const container = tabsRef?.current;
    const node = tabRefs.current[activeTabId];
    if (!container || !node) return;
    const cRect = container.getBoundingClientRect();
    const nRect = node.getBoundingClientRect();
    if (nRect.left < cRect.left + 8) {
      container.scrollTo({ left: container.scrollLeft + (nRect.left - cRect.left) - 12, behavior: 'smooth' });
    } else if (nRect.right > cRect.right - 8) {
      container.scrollTo({ left: container.scrollLeft + (nRect.right - cRect.right) + 12, behavior: 'smooth' });
    }
  }, [activeTabId, tabsRef]);

  return (
    <div
      ref={menuRef}
      className={`rdp-stickyMenu ${isStuck ? 'rdp-stickyMenu--stuck' : ''}`}
      style={fixedStyle}
    >
      {search ? <div className="rdp-stickyMenu-search">{search}</div> : null}
      <div className="rdp-stickyMenu-tabsrow">
        {showPrev ? (
          <button
            className="rdp-tabs-prev"
            type="button"
            aria-label="Scroll categories back"
            onClick={onScrollTabsPrev}
          >
            <span className="rdp-tabs-prev-chevron" aria-hidden="true">‹</span>
          </button>
        ) : null}
        <nav className="rdp-tabs" aria-label="Menu categories">
          <div ref={tabsRef} className="rdp-tabs-scroll" role="presentation">
            {tabs.map((t) => (
              <button
                key={t.id}
                ref={(node) => {
                  if (node) tabRefs.current[t.id] = node;
                  else delete tabRefs.current[t.id];
                }}
                className={`rdp-tab ${activeTabId === t.id ? 'rdp-tab--active' : ''}`}
                type="button"
                onClick={() => onSelectTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </nav>

        {showNext ? (
          <button
            className="rdp-tabs-next"
            type="button"
            aria-label="Scroll categories"
            onClick={onScrollTabsNext}
          >
            <span className="rdp-tabs-next-chevron" aria-hidden="true">›</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

